import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Empty, Space, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { normalizeRadiusAction, radiusActionLabel, radiusIdentitySummary } from "../../pages/enduser/radius-policies/radiusPolicyModel";
import { deviceDisplayName, devicePolicyValue, policyDeviceScope } from "./radiusDeviceModel";

const { Text } = Typography;

const ACTION_COLOR = { allow: "green", mfa_required: "gold", block: "red" };
const DRAFT_KEYS = ["policyDraft.basicPolicyInformation", "editPolicyData", "formSessionData"];

// The policies that apply to one device: those that name it, then those that cover every
// device. It is a view over the same RADIUS policies the Policies page lists -- a policy
// "for this device" is an ordinary policy whose Network devices field names it.
export default function DevicePoliciesDrawer({ open, device, policies, onClose }) {
  const navigate = useNavigate();

  const rows = useMemo(() => {
    if (!device) return [];
    return (policies || [])
      .map((p) => ({ p, scope: policyDeviceScope(p, device) }))
      .filter((r) => r.scope)
      .sort((a, b) => (a.scope === b.scope ? 0 : a.scope === "this" ? -1 : 1));
  }, [policies, device]);

  const addPolicyForDevice = () => {
    DRAFT_KEYS.forEach((k) => localStorage.removeItem(k));
    navigate("/pam/policy/v2/createRadiusPolicy", { state: { prefillClients: [devicePolicyValue(device)] } });
  };

  return (
    <Drawer
      title={device ? `Policies for ${deviceDisplayName(device)}` : "Policies"}
      open={open}
      onClose={onClose}
      width={560}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={addPolicyForDevice} disabled={!device}>
          Add policy for this device
        </Button>
      }
    >
      {rows.length === 0 ? (
        <Empty description="No policy applies to this device. Logins from it are allowed without a second factor." />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          {rows.map(({ p, scope }) => {
            const flow = normalizeRadiusAction(p?.policyJson?.policyFlow);
            const paused = p.status === "Suspended";
            return (
              <div
                key={p.id}
                style={{ border: "1px solid var(--an-line2)", borderRadius: 8, padding: "10px 14px", opacity: paused ? 0.6 : 1 }}
              >
                <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
                  <Text strong>{p.policyName}</Text>
                  <Space size={6}>
                    <Tag color={ACTION_COLOR[flow] || "default"}>{radiusActionLabel(flow)}</Tag>
                    {paused && <Tag>Paused</Tag>}
                  </Space>
                </Space>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">
                    {radiusIdentitySummary(p.policyJson)} · {scope === "this" ? "this device" : "all devices"}
                  </Text>
                </div>
              </div>
            );
          })}
        </Space>
      )}
    </Drawer>
  );
}
